import {
  CHUNK_SIZE,
  chunkKeyInt,
  createNoiseGenerator,
  TILE_SIZE,
  TileType,
  type TileTypeValue,
} from '@dreamgame/shared';
import { Container, Graphics, Matrix, type Texture } from 'pixi.js';
import { type ChunkWithObjects, getAllChunks } from '../../state/chunks';
import { getCamera } from '../Camera';
import { COLORS, TILE_COLORS, TILE_TEXTURE_NAMES } from '../constants';
import { loadTexture, onCategoryReload } from '../TextureManager';
import type { VisibleChunk } from './types';

// ============================================================================
// TileLayer - Renders terrain tiles
// ============================================================================
// PERFORMANCE: Each chunk's tiles are drawn ONCE into a persistent Graphics
// object and cached. Only rebuilt when the chunk's tile data changes (effectively
// on initial load only, since terrain is static). This reduces per-frame work
// from O(thousands of draw calls) to O(visible_chunks visibility toggles).
// ============================================================================

let container: Container;
let unloadedGraphics: Graphics; // Ephemeral: cleared each frame for placeholder rects

// Tile textures: base + variants per tile type
const tileTextures: Map<TileTypeValue, Texture[]> = new Map();

// Max number of variants to try loading per tile type (base + v1..v3)
const TILE_VARIANT_COUNT = 5;

// Per-tile variant blacklist for known bad textures.
const DISABLED_TILE_VARIANTS: ReadonlyMap<TileTypeValue, ReadonlySet<number>> = new Map([
  [TileType.GRASS, new Set([2, 3])],
]);

// Forest tiles get a secondary blended texture pass to break repetition
const FOREST_BLEND_TILES: ReadonlySet<TileTypeValue> = new Set([
  TileType.FOREST_FLOOR,
  TileType.FOREST_GRASS,
  TileType.PINE_NEEDLES,
]);

// Forest ground can be rendered procedurally to avoid tile-texture repetition.
const USE_PROCEDURAL_FOREST_GROUND = true;

const PROCEDURAL_FOREST_TILES: ReadonlySet<TileTypeValue> = new Set([
  TileType.FOREST_FLOOR,
  TileType.FOREST_GRASS,
  TileType.PINE_NEEDLES,
]);

// PERFORMANCE: Cached per-chunk Graphics objects
interface CachedChunkLodEntry {
  graphics: Graphics;
  tilesRef: Uint8Array; // Track tile data reference for cache invalidation
}

interface CachedChunkEntry {
  lodEntries: Map<TileLodLevel, CachedChunkLodEntry>;
}

const _chunkGraphicsCache = new Map<number, CachedChunkEntry>();
const _usedChunkKeys: Set<number> = new Set();

type TileLodLevel = 0 | 1 | 2;

export const createTileLayer = (): Container => {
  container = new Container();
  unloadedGraphics = new Graphics();
  container.addChild(unloadedGraphics);
  return container;
};

export const loadTileTextures = async (): Promise<void> => {
  try {
    for (const { type, name } of TILE_TEXTURE_NAMES) {
      const variants: Texture[] = [];

      // Load base texture
      try {
        const texture = await loadTexture({ path: `/tiles/${name}.png` });
        variants.push(texture);
      } catch {
        console.warn(`Tile texture not found: /tiles/${name}.png`);
        continue;
      }

      // Load variant textures (silently skip missing)
      const disabledVariants = DISABLED_TILE_VARIANTS.get(type);
      for (let v = 1; v <= TILE_VARIANT_COUNT; v++) {
        if (disabledVariants?.has(v)) {
          continue;
        }

        try {
          const texture = await loadTexture({ path: `/tiles/${name}_v${v}.png` });
          variants.push(texture);
        } catch {
          // Variant not found, skip silently
        }
      }

      if (variants.length > 0) {
        tileTextures.set(type, variants);
      }
    }
    console.log(
      `Loaded tile textures for ${tileTextures.size} types (${[...tileTextures.values()].reduce((sum, v) => sum + v.length, 0)} total)`
    );
  } catch (error) {
    console.warn('Failed to load tile textures, using fallback colors', error);
  }
};

// Reload textures (clears cache and reloads from TextureManager)
export const reloadTileTextures = async (): Promise<void> => {
  // Clear texture cache
  tileTextures.clear();

  // Invalidate all cached chunk graphics so they rebuild with new textures
  for (const chunkEntry of _chunkGraphicsCache.values()) {
    for (const lodEntry of chunkEntry.lodEntries.values()) {
      lodEntry.tilesRef = new Uint8Array(0); // Force rebuild on next frame
    }
  }

  // Reload textures
  await loadTileTextures();
  console.log('Tile textures reloaded');
};

// Register for texture reload events
onCategoryReload({ category: 'tiles', callback: reloadTileTextures });

const TILE_OVERLAP = 0.5; // Prevent subpixel gaps

// Textures are 128x128, tiles are 32x32
const TEXTURE_SCALE = TILE_SIZE / 128;

// Variant selection and blend constants
const VARIANT_MACRO_SIZE = 5; // 5x5 tile regions share a dominant variant
const SECONDARY_BLEND_ALPHA_MIN = 0.12;
const SECONDARY_BLEND_ALPHA_RANGE = 0.12;
const SECONDARY_UV_JITTER_PX = 32;

// Procedural forest ground config (splat-map style, no repeated tile textures)
const FOREST_SUBTILE_COUNT = 2; // Split each 32x32 tile into smaller cells
const FOREST_SUBTILE_OVERLAP = 0.35;

const _forestMacroNoise = createNoiseGenerator({ seed: 9011 });
const _forestDetailNoise = createNoiseGenerator({ seed: 9019 });
const _forestMoistureNoise = createNoiseGenerator({ seed: 9029 });
const _forestGrainNoise = createNoiseGenerator({ seed: 9041 });
const _forestWarpXNoise = createNoiseGenerator({ seed: 9049 });
const _forestWarpYNoise = createNoiseGenerator({ seed: 9059 });

interface ForestProceduralPalette {
  readonly grass: number;
  readonly darkGrass: number;
  readonly dirt: number;
  readonly puddle: number;
  readonly moss: number;
}

type ForestProceduralTileType = (typeof TileType)['FOREST_FLOOR' | 'FOREST_GRASS' | 'PINE_NEEDLES'];

const FOREST_PROCEDURAL_PALETTES: Record<ForestProceduralTileType, ForestProceduralPalette> = {
  [TileType.FOREST_FLOOR]: {
    grass: 0x56763a,
    darkGrass: 0x2c4a27,
    dirt: 0x735a36,
    puddle: 0x30473a,
    moss: 0x6f8744,
  },
  [TileType.FOREST_GRASS]: {
    grass: 0x6b9147,
    darkGrass: 0x35552a,
    dirt: 0x81643a,
    puddle: 0x355148,
    moss: 0x7ea24d,
  },
  [TileType.PINE_NEEDLES]: {
    grass: 0x66723b,
    darkGrass: 0x3f4a2c,
    dirt: 0x825f39,
    puddle: 0x2f4133,
    moss: 0x718043,
  },
};

// Deterministic hash utilities for terrain texturing
const tileHash = ({
  worldTileX,
  worldTileY,
  seed,
}: {
  worldTileX: number;
  worldTileY: number;
  seed: number;
}): number => {
  return ((worldTileX * 73856093) ^ (worldTileY * 19349663) ^ (seed * 83492791)) >>> 0;
};

// Choose a primary variant using macro-regional coherence + local breakup.
const primaryVariantIndex = ({
  worldTileX,
  worldTileY,
  variantCount,
}: {
  worldTileX: number;
  worldTileY: number;
  variantCount: number;
}): number => {
  if (variantCount <= 1) {
    return 0;
  }

  const macroX = Math.floor(worldTileX / VARIANT_MACRO_SIZE);
  const macroY = Math.floor(worldTileY / VARIANT_MACRO_SIZE);
  const regional = tileHash({ worldTileX: macroX, worldTileY: macroY, seed: 11 }) % variantCount;
  const local = tileHash({ worldTileX, worldTileY, seed: 17 }) % variantCount;
  const selector = tileHash({ worldTileX, worldTileY, seed: 29 }) % 1000;

  // Keep most tiles on the regional variant, with local deviations.
  if (selector < 680) {
    return regional;
  }
  return local;
};

// Pick a distinct secondary variant for low-alpha blending.
const secondaryVariantIndex = ({
  worldTileX,
  worldTileY,
  variantCount,
  primaryIndex,
}: {
  worldTileX: number;
  worldTileY: number;
  variantCount: number;
  primaryIndex: number;
}): number => {
  if (variantCount <= 1) {
    return primaryIndex;
  }
  const step = 1 + (tileHash({ worldTileX, worldTileY, seed: 37 }) % (variantCount - 1));
  return (primaryIndex + step) % variantCount;
};

const secondaryBlendAlpha = ({
  worldTileX,
  worldTileY,
}: {
  worldTileX: number;
  worldTileY: number;
}): number => {
  const blendNoise = tileHash({ worldTileX, worldTileY, seed: 41 }) % 1000;
  return SECONDARY_BLEND_ALPHA_MIN + (blendNoise / 1000) * SECONDARY_BLEND_ALPHA_RANGE;
};

const computeTileLodLevel = (): TileLodLevel => {
  const zoom = getCamera().zoom;
  if (zoom < 0.42) {
    return 2;
  }
  if (zoom < 0.75) {
    return 1;
  }
  return 0;
};

const clamp01 = ({ value }: { value: number }): number => {
  if (value < 0) {
    return 0;
  }
  if (value > 1) {
    return 1;
  }
  return value;
};

const noiseTo01 = ({ value }: { value: number }): number => {
  return clamp01({ value: (value + 1) * 0.5 });
};

const mixColor = ({ from, to, t }: { from: number; to: number; t: number }): number => {
  const clampedT = clamp01({ value: t });
  const fr = (from >> 16) & 0xff;
  const fg = (from >> 8) & 0xff;
  const fb = from & 0xff;
  const tr = (to >> 16) & 0xff;
  const tg = (to >> 8) & 0xff;
  const tb = to & 0xff;

  const r = Math.round(fr + (tr - fr) * clampedT);
  const g = Math.round(fg + (tg - fg) * clampedT);
  const b = Math.round(fb + (tb - fb) * clampedT);

  return (r << 16) | (g << 8) | b;
};

const shadeColor = ({ color, amount }: { color: number; amount: number }): number => {
  if (amount >= 0) {
    return mixColor({ from: color, to: 0xffffff, t: amount });
  }
  return mixColor({ from: color, to: 0x000000, t: -amount });
};

const sampleForestSplatColor = ({
  tileType,
  sampleTileX,
  sampleTileY,
  lodLevel,
}: {
  tileType: ForestProceduralTileType;
  sampleTileX: number;
  sampleTileY: number;
  lodLevel: TileLodLevel;
}): number => {
  const warpX =
    _forestWarpXNoise.noise2D({ x: sampleTileX * 0.085, y: sampleTileY * 0.085 }) * 0.38;
  const warpY =
    _forestWarpYNoise.noise2D({ x: sampleTileX * 0.085, y: sampleTileY * 0.085 }) * 0.38;
  const warpedX = sampleTileX + warpX;
  const warpedY = sampleTileY + warpY;

  const macro = noiseTo01({
    value: _forestMacroNoise.noise2D({ x: warpedX * 0.055, y: warpedY * 0.055 }),
  });
  const detailFrequency = lodLevel === 0 ? 0.2 : lodLevel === 1 ? 0.12 : 0.08;
  const detail = noiseTo01({
    value: _forestDetailNoise.noise2D({
      x: warpedX * detailFrequency,
      y: warpedY * detailFrequency,
    }),
  });
  const moisture = noiseTo01({
    value: _forestMoistureNoise.noise2D({ x: warpedX * 0.072, y: warpedY * 0.072 }),
  });
  const grainFrequency = lodLevel === 0 ? 0.55 : lodLevel === 1 ? 0.24 : 0.12;
  const grain = noiseTo01({
    value: _forestGrainNoise.noise2D({
      x: warpedX * grainFrequency,
      y: warpedY * grainFrequency,
    }),
  });

  const palette = FOREST_PROCEDURAL_PALETTES[tileType];
  if (!palette) {
    return TILE_COLORS[tileType] ?? 0x000000;
  }

  const detailSuppression = lodLevel === 0 ? 1 : lodLevel === 1 ? 0.75 : 0.55;
  const darkWeight = clamp01({
    value: (macro - 0.5) * 1.8 + (moisture - 0.46) * 0.9 * detailSuppression,
  });
  const dirtWeight = clamp01({
    value: (0.5 - macro) * 2.0 + (detail - 0.56) * 0.75 * detailSuppression,
  });
  const mossWeight = clamp01({ value: (moisture - 0.57) * 1.7 + (0.55 - detail) * 0.65 });
  const puddleWeight = clamp01({ value: (0.3 - moisture) * 3.2 + (0.43 - macro) * 1.4 });

  let color = mixColor({
    from: palette.grass,
    to: palette.moss,
    t: mossWeight * 0.5,
  });
  color = mixColor({
    from: color,
    to: palette.darkGrass,
    t: darkWeight * 0.82,
  });
  color = mixColor({
    from: color,
    to: palette.dirt,
    t: dirtWeight * 0.72,
  });
  color = mixColor({
    from: color,
    to: palette.puddle,
    t: puddleWeight * 0.58,
  });

  const shadeScale = lodLevel === 0 ? 0.18 : lodLevel === 1 ? 0.1 : 0.05;
  const shadeAmount = (grain - 0.5) * shadeScale;
  return shadeColor({ color, amount: shadeAmount });
};

// Draw placeholder for unloaded chunk (into shared unloadedGraphics, cleared each frame)
const drawUnloadedChunk = ({ chunkX, chunkY }: { chunkX: number; chunkY: number }): void => {
  const worldX = chunkX * CHUNK_SIZE * TILE_SIZE;
  const worldY = chunkY * CHUNK_SIZE * TILE_SIZE;
  const size = CHUNK_SIZE * TILE_SIZE;

  unloadedGraphics.setFillStyle({ color: COLORS.UNLOADED_CHUNK, alpha: 0.5 });
  unloadedGraphics.rect(worldX, worldY, size, size);
  unloadedGraphics.fill();
};

// Edge transition constants
const EDGE_TRANSITION_SIZE = 4; // 4px blend strip
const EDGE_TRANSITION_ALPHA = 0.25;

// PERFORMANCE: Preallocated temp for chunkKeyInt in cross-chunk lookups
const _chunkKeyTemp = { x: 0, y: 0 };

// Get tile type for a neighbor, handling cross-chunk boundaries
const getNeighborTileType = ({
  chunkX,
  chunkY,
  localX,
  localY,
  offsetX,
  offsetY,
  loadedChunks,
  currentChunk,
}: {
  chunkX: number;
  chunkY: number;
  localX: number;
  localY: number;
  offsetX: number;
  offsetY: number;
  loadedChunks: ReadonlyMap<number, ChunkWithObjects>;
  currentChunk: ChunkWithObjects;
}): TileTypeValue => {
  const nx = localX + offsetX;
  const ny = localY + offsetY;

  // Fast path: neighbor in same chunk
  if (nx >= 0 && nx < CHUNK_SIZE && ny >= 0 && ny < CHUNK_SIZE) {
    const index = ny * CHUNK_SIZE + nx;
    return (currentChunk.tiles[index] ?? TileType.VOID) as TileTypeValue;
  }

  // Slow path: neighbor in different chunk
  let cx = chunkX;
  let cy = chunkY;
  let nnx = nx;
  let nny = ny;

  if (nnx < 0) {
    cx--;
    nnx += CHUNK_SIZE;
  } else if (nnx >= CHUNK_SIZE) {
    cx++;
    nnx -= CHUNK_SIZE;
  }
  if (nny < 0) {
    cy--;
    nny += CHUNK_SIZE;
  } else if (nny >= CHUNK_SIZE) {
    cy++;
    nny -= CHUNK_SIZE;
  }

  _chunkKeyTemp.x = cx;
  _chunkKeyTemp.y = cy;
  const neighborChunk = loadedChunks.get(chunkKeyInt(_chunkKeyTemp));
  if (!neighborChunk) {
    return TileType.VOID as TileTypeValue;
  }

  const index = nny * CHUNK_SIZE + nnx;
  return (neighborChunk.tiles[index] ?? TileType.VOID) as TileTypeValue;
};

// Draw edge transitions for a single tile (semi-transparent blending strips at biome borders)
const drawEdgeTransitions = ({
  g,
  chunkX,
  chunkY,
  localX,
  localY,
  worldX,
  worldY,
  tileType,
  loadedChunks,
  currentChunk,
}: {
  g: Graphics;
  chunkX: number;
  chunkY: number;
  localX: number;
  localY: number;
  worldX: number;
  worldY: number;
  tileType: TileTypeValue;
  loadedChunks: ReadonlyMap<number, ChunkWithObjects>;
  currentChunk: ChunkWithObjects;
}): void => {
  // Check 4 cardinal neighbors
  const offsets = [
    { dx: 0, dy: -1, rx: worldX, ry: worldY, rw: TILE_SIZE, rh: EDGE_TRANSITION_SIZE }, // top
    {
      dx: 0,
      dy: 1,
      rx: worldX,
      ry: worldY + TILE_SIZE - EDGE_TRANSITION_SIZE,
      rw: TILE_SIZE,
      rh: EDGE_TRANSITION_SIZE,
    }, // bottom
    { dx: -1, dy: 0, rx: worldX, ry: worldY, rw: EDGE_TRANSITION_SIZE, rh: TILE_SIZE }, // left
    {
      dx: 1,
      dy: 0,
      rx: worldX + TILE_SIZE - EDGE_TRANSITION_SIZE,
      ry: worldY,
      rw: EDGE_TRANSITION_SIZE,
      rh: TILE_SIZE,
    }, // right
  ];

  for (const { dx, dy, rx, ry, rw, rh } of offsets) {
    const neighborType = getNeighborTileType({
      chunkX,
      chunkY,
      localX,
      localY,
      offsetX: dx,
      offsetY: dy,
      loadedChunks,
      currentChunk,
    });

    if (neighborType !== tileType && neighborType !== TileType.VOID) {
      const neighborColor = TILE_COLORS[neighborType] ?? 0x000000;
      g.setFillStyle({ color: neighborColor, alpha: EDGE_TRANSITION_ALPHA });
      g.rect(rx, ry, rw, rh);
      g.fill();
    }
  }
};

const drawTileRect = ({
  g,
  worldX,
  worldY,
}: {
  g: Graphics;
  worldX: number;
  worldY: number;
}): void => {
  g.rect(
    worldX - TILE_OVERLAP,
    worldY - TILE_OVERLAP,
    TILE_SIZE + TILE_OVERLAP * 2,
    TILE_SIZE + TILE_OVERLAP * 2
  );
  g.fill();
};

const drawSolidTile = ({
  g,
  worldX,
  worldY,
  tileType,
}: {
  g: Graphics;
  worldX: number;
  worldY: number;
  tileType: TileTypeValue;
}): void => {
  g.setFillStyle({ color: TILE_COLORS[tileType] ?? 0x000000 });
  drawTileRect({ g, worldX, worldY });
};

const drawProceduralForestTile = ({
  g,
  worldX,
  worldY,
  worldTileX,
  worldTileY,
  tileType,
  lodLevel,
}: {
  g: Graphics;
  worldX: number;
  worldY: number;
  worldTileX: number;
  worldTileY: number;
  tileType: ForestProceduralTileType;
  lodLevel: TileLodLevel;
}): void => {
  const subTileCount = lodLevel >= 2 ? 1 : FOREST_SUBTILE_COUNT;
  const subTileSize = TILE_SIZE / subTileCount;
  const subTileOverlap = lodLevel >= 2 ? 0.5 : FOREST_SUBTILE_OVERLAP;

  for (let subY = 0; subY < subTileCount; subY++) {
    for (let subX = 0; subX < subTileCount; subX++) {
      const sampleTileX = worldTileX + (subX + 0.5) / subTileCount;
      const sampleTileY = worldTileY + (subY + 0.5) / subTileCount;
      const color = sampleForestSplatColor({
        tileType,
        sampleTileX,
        sampleTileY,
        lodLevel,
      });
      const cellX = worldX + subX * subTileSize;
      const cellY = worldY + subY * subTileSize;

      g.setFillStyle({ color });
      g.rect(
        cellX - subTileOverlap,
        cellY - subTileOverlap,
        subTileSize + subTileOverlap * 2,
        subTileSize + subTileOverlap * 2
      );
      g.fill();
    }
  }
};

const drawPrimaryTextureTile = ({
  g,
  worldX,
  worldY,
  texture,
  lodLevel,
}: {
  g: Graphics;
  worldX: number;
  worldY: number;
  texture: Texture;
  lodLevel: TileLodLevel;
}): void => {
  // Use mipmaps + linear minification to avoid shimmering/moire when zoomed out.
  if (lodLevel > 0) {
    texture.source.autoGenerateMipmaps = true;
    texture.source.minFilter = 'linear';
    texture.source.mipmapFilter = 'linear';
  } else {
    texture.source.minFilter = 'nearest';
    texture.source.mipmapFilter = 'nearest';
  }

  g.setFillStyle({
    texture,
    matrix: new Matrix(TEXTURE_SCALE, 0, 0, TEXTURE_SCALE, worldX, worldY),
  });
  drawTileRect({ g, worldX, worldY });
};

const drawSecondaryForestBlend = ({
  g,
  worldX,
  worldY,
  worldTileX,
  worldTileY,
  texture,
  lodLevel,
}: {
  g: Graphics;
  worldX: number;
  worldY: number;
  worldTileX: number;
  worldTileY: number;
  texture: Texture;
  lodLevel: TileLodLevel;
}): void => {
  if (lodLevel > 0) {
    texture.source.autoGenerateMipmaps = true;
    texture.source.minFilter = 'linear';
    texture.source.mipmapFilter = 'linear';
  } else {
    texture.source.minFilter = 'nearest';
    texture.source.mipmapFilter = 'nearest';
  }

  const jitterX = tileHash({ worldTileX, worldTileY, seed: 47 }) % SECONDARY_UV_JITTER_PX;
  const jitterY = tileHash({ worldTileX, worldTileY, seed: 53 }) % SECONDARY_UV_JITTER_PX;
  const blendAlpha =
    lodLevel === 0 ? secondaryBlendAlpha({ worldTileX, worldTileY }) : SECONDARY_BLEND_ALPHA_MIN;
  g.setFillStyle({
    texture,
    alpha: blendAlpha,
    matrix: new Matrix(
      TEXTURE_SCALE,
      0,
      0,
      TEXTURE_SCALE,
      worldX - jitterX * TEXTURE_SCALE,
      worldY - jitterY * TEXTURE_SCALE
    ),
  });
  drawTileRect({ g, worldX, worldY });
};

const drawTexturedTile = ({
  g,
  tileType,
  worldX,
  worldY,
  worldTileX,
  worldTileY,
  variants,
  lodLevel,
}: {
  g: Graphics;
  tileType: TileTypeValue;
  worldX: number;
  worldY: number;
  worldTileX: number;
  worldTileY: number;
  variants: Texture[];
  lodLevel: TileLodLevel;
}): void => {
  const variantCount = variants.length;
  const primaryIndex = primaryVariantIndex({ worldTileX, worldTileY, variantCount });
  const primaryTexture = variants[primaryIndex] ?? variants[0];
  if (!primaryTexture) {
    drawSolidTile({ g, worldX, worldY, tileType });
    return;
  }

  drawPrimaryTextureTile({ g, worldX, worldY, texture: primaryTexture, lodLevel });
  if (lodLevel >= 2 || !FOREST_BLEND_TILES.has(tileType) || variantCount <= 1) {
    return;
  }

  const secondaryIndex = secondaryVariantIndex({
    worldTileX,
    worldTileY,
    variantCount,
    primaryIndex,
  });
  const secondaryTexture = variants[secondaryIndex];
  if (!secondaryTexture || secondaryIndex === primaryIndex) {
    return;
  }

  drawSecondaryForestBlend({
    g,
    worldX,
    worldY,
    worldTileX,
    worldTileY,
    texture: secondaryTexture,
    lodLevel,
  });
};

// Draw base tiles (textured or color) for a chunk
const drawBaseTiles = ({
  g,
  chunk,
  chunkX,
  chunkY,
  lodLevel,
}: {
  g: Graphics;
  chunk: ChunkWithObjects;
  chunkX: number;
  chunkY: number;
  lodLevel: TileLodLevel;
}): void => {
  const chunkWorldX = chunkX * CHUNK_SIZE * TILE_SIZE;
  const chunkWorldY = chunkY * CHUNK_SIZE * TILE_SIZE;

  for (let localY = 0; localY < CHUNK_SIZE; localY++) {
    for (let localX = 0; localX < CHUNK_SIZE; localX++) {
      const tileIndex = localY * CHUNK_SIZE + localX;
      const tileType = (chunk.tiles[tileIndex] ?? TileType.VOID) as TileTypeValue;
      const worldTileX = chunkX * CHUNK_SIZE + localX;
      const worldTileY = chunkY * CHUNK_SIZE + localY;
      const worldX = chunkWorldX + localX * TILE_SIZE;
      const worldY = chunkWorldY + localY * TILE_SIZE;
      const variants = tileTextures.get(tileType);

      if (USE_PROCEDURAL_FOREST_GROUND && PROCEDURAL_FOREST_TILES.has(tileType)) {
        drawProceduralForestTile({
          g,
          worldX,
          worldY,
          worldTileX,
          worldTileY,
          tileType: tileType as ForestProceduralTileType,
          lodLevel,
        });
        continue;
      }

      if (variants && variants.length > 0) {
        drawTexturedTile({
          g,
          tileType,
          worldX,
          worldY,
          worldTileX,
          worldTileY,
          variants,
          lodLevel,
        });
      } else {
        drawSolidTile({ g, worldX, worldY, tileType });
      }
    }
  }
};

// Draw edge transition blend strips at biome borders for a chunk
const drawChunkEdgeTransitions = ({
  g,
  chunk,
  chunkX,
  chunkY,
  loadedChunks,
}: {
  g: Graphics;
  chunk: ChunkWithObjects;
  chunkX: number;
  chunkY: number;
  loadedChunks: ReadonlyMap<number, ChunkWithObjects>;
}): void => {
  const chunkWorldX = chunkX * CHUNK_SIZE * TILE_SIZE;
  const chunkWorldY = chunkY * CHUNK_SIZE * TILE_SIZE;

  for (let localY = 0; localY < CHUNK_SIZE; localY++) {
    for (let localX = 0; localX < CHUNK_SIZE; localX++) {
      const tileIndex = localY * CHUNK_SIZE + localX;
      const tileType = (chunk.tiles[tileIndex] ?? TileType.VOID) as TileTypeValue;
      if (tileType === TileType.VOID) {
        continue;
      }

      const worldX = chunkWorldX + localX * TILE_SIZE;
      const worldY = chunkWorldY + localY * TILE_SIZE;

      drawEdgeTransitions({
        g,
        chunkX,
        chunkY,
        localX,
        localY,
        worldX,
        worldY,
        tileType,
        loadedChunks,
        currentChunk: chunk,
      });
    }
  }
};

// Draw all tiles for a chunk (called once per chunk load, then cached).
// Not a hot path.
const drawChunkTilesInto = ({
  g,
  chunk,
  chunkX,
  chunkY,
  loadedChunks,
  lodLevel,
}: {
  g: Graphics;
  chunk: ChunkWithObjects;
  chunkX: number;
  chunkY: number;
  loadedChunks: ReadonlyMap<number, ChunkWithObjects>;
  lodLevel: TileLodLevel;
}): void => {
  drawBaseTiles({ g, chunk, chunkX, chunkY, lodLevel });
  drawChunkEdgeTransitions({ g, chunk, chunkX, chunkY, loadedChunks });
};

const getOrCreateCachedChunkEntry = ({ chunkKey }: { chunkKey: number }): CachedChunkEntry => {
  const cachedChunk = _chunkGraphicsCache.get(chunkKey);
  if (cachedChunk) {
    return cachedChunk;
  }

  const newEntry: CachedChunkEntry = {
    lodEntries: new Map<TileLodLevel, CachedChunkLodEntry>(),
  };
  _chunkGraphicsCache.set(chunkKey, newEntry);
  return newEntry;
};

const hideInactiveLodGraphics = ({
  cachedChunk,
  activeLodLevel,
}: {
  cachedChunk: CachedChunkEntry;
  activeLodLevel: TileLodLevel;
}): void => {
  for (const [cachedLod, entry] of cachedChunk.lodEntries) {
    if (cachedLod !== activeLodLevel && entry.graphics.visible) {
      entry.graphics.visible = false;
    }
  }
};

const getOrRebuildLodEntry = ({
  cachedChunk,
  chunk,
  chunkX,
  chunkY,
  loadedChunks,
  lodLevel,
}: {
  cachedChunk: CachedChunkEntry;
  chunk: ChunkWithObjects;
  chunkX: number;
  chunkY: number;
  loadedChunks: ReadonlyMap<number, ChunkWithObjects>;
  lodLevel: TileLodLevel;
}): CachedChunkLodEntry => {
  const existingEntry = cachedChunk.lodEntries.get(lodLevel);
  if (!existingEntry) {
    const graphics = new Graphics();
    drawChunkTilesInto({ g: graphics, chunk, chunkX, chunkY, loadedChunks, lodLevel });
    container.addChild(graphics);

    const newEntry: CachedChunkLodEntry = { graphics, tilesRef: chunk.tiles };
    cachedChunk.lodEntries.set(lodLevel, newEntry);
    return newEntry;
  }

  if (existingEntry.tilesRef !== chunk.tiles) {
    existingEntry.graphics.clear();
    drawChunkTilesInto({
      g: existingEntry.graphics,
      chunk,
      chunkX,
      chunkY,
      loadedChunks,
      lodLevel,
    });
    existingEntry.tilesRef = chunk.tiles;
  }

  return existingEntry;
};

const drawVisibleChunkTiles = ({
  chunkX,
  chunkY,
  lodLevel,
  loadedChunks,
}: {
  chunkX: number;
  chunkY: number;
  lodLevel: TileLodLevel;
  loadedChunks: ReadonlyMap<number, ChunkWithObjects>;
}): void => {
  const chunkKey = chunkKeyInt({ x: chunkX, y: chunkY });
  _usedChunkKeys.add(chunkKey);

  const chunk = loadedChunks.get(chunkKey);
  if (!chunk) {
    drawUnloadedChunk({ chunkX, chunkY });
    return;
  }

  const cachedChunk = getOrCreateCachedChunkEntry({ chunkKey });
  hideInactiveLodGraphics({ cachedChunk, activeLodLevel: lodLevel });
  const lodEntry = getOrRebuildLodEntry({
    cachedChunk,
    chunk,
    chunkX,
    chunkY,
    loadedChunks,
    lodLevel,
  });
  lodEntry.graphics.visible = true;
};

const cleanupChunkTileCache = ({
  loadedChunks,
}: {
  loadedChunks: ReadonlyMap<number, ChunkWithObjects>;
}): void => {
  for (const [key, chunkEntry] of _chunkGraphicsCache) {
    if (!loadedChunks.has(key)) {
      for (const lodEntry of chunkEntry.lodEntries.values()) {
        container.removeChild(lodEntry.graphics);
        lodEntry.graphics.destroy();
      }
      _chunkGraphicsCache.delete(key);
      continue;
    }

    if (!_usedChunkKeys.has(key)) {
      for (const lodEntry of chunkEntry.lodEntries.values()) {
        if (lodEntry.graphics.visible) {
          lodEntry.graphics.visible = false;
        }
      }
    }
  }
};

export const drawTiles = ({
  visibleChunks,
}: {
  visibleChunks: ReadonlyArray<VisibleChunk>;
}): void => {
  const lodLevel = computeTileLodLevel();

  // Only the unloaded placeholder graphics are redrawn each frame
  unloadedGraphics.clear();
  _usedChunkKeys.clear();

  const loadedChunks = getAllChunks();

  for (const { x: chunkX, y: chunkY } of visibleChunks) {
    drawVisibleChunkTiles({ chunkX, chunkY, lodLevel, loadedChunks });
  }

  cleanupChunkTileCache({ loadedChunks });
};
